import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import List from 'components/atoms/List';
import SectionSeparator from 'components/atoms/SectionSeparator.component';
import { Title } from 'components/atoms/Title';
import { SizesEnum } from 'components/atoms/Title/Title.enum';
import {
  getMonthlyAmount,
  isDowngradingSubscriptionPlan,
} from 'components/pages/Subscriptions/Subscription.helper';
import useCustomerCountry from 'hooks/shared/useCustomerCountry';
import useStripeSubscription from 'hooks/Subscriptions/useStripeSubscription';
import useSubscriptionPlanInfo from 'hooks/Subscriptions/useSubscriptionPlanInfo';
import useFeatures from 'hooks/useFeatures';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { SubscriptionPlan } from 'types/subscriptions.types';
import { SubscriptionPlanNameEnum } from 'types/users.types';
import { excludeNumbers } from 'utils/helpers';
import { round } from 'utils/math';

const PlanWidget: FC<{
  plan: Partial<SubscriptionPlan>;
  currentPlan: SubscriptionPlanNameEnum;
  customFeatures?: {
    content: string;
  }[];
  onClickSubscribe?: () => void;
}> = ({ plan, currentPlan, customFeatures, onClickSubscribe }) => {
  const { t, i18n } = useTranslation();

  const country = useCustomerCountry();

  const getPlanFeatures = useFeatures('paywall.getPlanFeatures');

  const { shortNickname, nickname, interval, amount } = plan;

  const planGroup = excludeNumbers(shortNickname);

  const _interval = shortNickname?.slice((shortNickname?.length || 0) - 2);

  const isDowngradingPlan = isDowngradingSubscriptionPlan(
    currentPlan,
    nickname as SubscriptionPlanNameEnum,
  );

  const isCurrentPlan = currentPlan === nickname;

  const { displayName, discountBadgeText, subtitle, badge, featuresTitle } =
    useSubscriptionPlanInfo(planGroup);

  const _amount = round(
    getMonthlyAmount(interval as string, amount as number),
    100,
  );

  const { Icon: BadgeIcon, text: patchText } = badge || {};

  const { subscribe, isLoading } = useStripeSubscription();

  const hasDiscountBadge =
    discountBadgeText && i18n.exists(discountBadgeText as string);

  const planFeatures =
    customFeatures ||
    getPlanFeatures(country)[nickname as string]?.features?.filter(Boolean);

  const shouldHideController =
    isDowngradingPlan ||
    (nickname?.includes('basic') && !isCurrentPlan) ||
    (nickname === SubscriptionPlanNameEnum.free &&
      currentPlan.includes('trial'));

  return (
    <div className="relative bg-white p-5 px-6 pb-32 flex-1 m-2 rounded-2xl hover:-translate-y-5 transition-all duration-500 border border-transparent hover:border-taxes">
      {hasDiscountBadge ? (
        <div className="absolute left-0 right-0 top-0 bottom-[65%] bg-transparent overflow-hidden">
          <div className="bg-expenses rotate-[30deg] absolute right-0 top-0 translate-x-52 translate-y-12 w-[460px] xl:w-[600px] text-center py-2">
            <span className="text-white font-bold text-base">
              {t(discountBadgeText as string)}
            </span>
          </div>
        </div>
      ) : null}
      {badge && !hasDiscountBadge ? (
        <div className="flex justify-end mb-3">
          <div className="flex items-center space-x-1 bg-bank-100 py-[6px] px-3 rounded-full border border-bank-400 w-fit">
            <BadgeIcon width={22} className="text-bank-900" />

            <span className="text-bank text-sm font-semibold">
              {t(patchText as string)}
            </span>
          </div>
        </div>
      ) : null}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-primary text-xl font-semibold">{displayName}</h1>

          <Title type={SizesEnum.h5} className="my-2 text-taxes">
            € {_amount}{' '}
            <span className="text-xl font-medium">
              / {t('subscriptions_screen.plans.month_abbr')}
            </span>{' '}
            {_amount > 0 ? (
              <span className="text-xs text-primary-500 font-normal tracking-normal">
                ({t('excl_vat')})
              </span>
            ) : null}
          </Title>

          <h2 className="text-primary-400 text-sm font-semibold">
            {t(subtitle) ||
              t(`subscriptions_screen.billed_${_interval === '12' ? 12 : 1}`)}
          </h2>
        </div>
      </div>

      <SectionSeparator className="my-4" />

      <h2 className="text-primary-400 text-sm font-semibold">
        {featuresTitle}
      </h2>

      <List
        data={
          (planFeatures || []) as {
            content: string;
          }[]
        }
        itemClassName="!pb-0 !pl-0 py-3 items-center"
        itemTextClassName="!pl-3 font-semibold"
        iconClassName="!text-primary !w-4 !h-4"
      />

      {shouldHideController ? null : (
        <div className="mt-8 absolute left-4 right-4 bottom-5 group">
          <Button
            color="taxes"
            type="button"
            className={classNames('w-full', {
              'bg-primary-400 hover:bg-primary-500': isCurrentPlan,
            })}
            disabled={isCurrentPlan}
            onClick={() => {
              onClickSubscribe?.();
              subscribe(plan);
            }}
            loading={isLoading}
          >
            {isCurrentPlan
              ? t('subscriptions_screen.plans.current_plan')
              : t('subscriptions_screen.plans.get_plan_cta', {
                  planName: displayName,
                })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PlanWidget;
